import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Edit3, Package, Plus, Trash2 } from 'lucide-react';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';
import { getIndustryTemplate } from '../../lib/industryTemplates';
import { useAuth } from '../../context/AuthContext';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';
const emptyForm = { name: '', sku: '', category: '', unit: 'litre', default_price: '', tax_percentage: 0 };
const defaultUnits = ['litre', 'kg', 'packet', 'piece', 'gm', 'can', 'service'];

export default function Products() {
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { business } = useAuth();
  const template = getIndustryTemplate(business?.business_type);

  const load = () => apiGet('/merchant/products').then(r => setList(r.data || [])).catch(e => toast.error(e.message));
  const loadCategories = () => apiGet('/merchant/categories').then(r => setCategories(r.data || [])).catch(() => setCategories([]));
  useEffect(() => { load(); loadCategories(); }, []);

  const selectedCategory = categories.find(c => c.name === form.category);
  const unitOptions = selectedCategory?.units?.length ? selectedCategory.units : defaultUnits;

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setSaving(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name || '',
      sku: product.sku || '',
      category: product.category || '',
      unit: product.unit || 'litre',
      default_price: product.default_price ?? '',
      tax_percentage: product.tax_percentage ?? 0,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || null,
        category: form.category.trim() || null,
        unit: form.unit,
        default_price: Number(form.default_price || 0),
        tax_percentage: Number(form.tax_percentage || 0),
      };
      if (editing) {
        await apiPatch(`/merchant/products/${editing.id}`, payload);
        toast.success('Product updated');
      } else {
        await apiPost('/merchant/products', payload);
        toast.success('Product added');
      }
      resetForm();
      await load();
      await loadCategories();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  const remove = async (product) => {
    if (!window.confirm(`Delete ${product.name}? Existing deliveries and invoices will keep their product history.`)) return;
    setDeletingId(product.id);
    try {
      await apiDelete(`/merchant/products/${product.id}`);
      toast.success('Product deleted');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Products</h1>
        <button onClick={openCreate} data-testid="add-product-btn" className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add product</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.length === 0 && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-rozops-text-muted col-span-full">{template.emptyProducts}</div>}
        {list.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5" data-testid={`product-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center mb-3"><Package className="w-5 h-5" /></div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => openEdit(p)} title="Edit product" aria-label={`Edit ${p.name}`} className="w-9 h-9 rounded-lg text-rozops-text-muted hover:bg-gray-50 hover:text-rozops-blue grid place-items-center" data-testid={`edit-product-${p.id}`}>
                  <Edit3 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(p)} disabled={deletingId === p.id} title="Delete product" aria-label={`Delete ${p.name}`} className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-50 grid place-items-center" data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="font-heading font-bold text-lg text-rozops-navy break-words">{p.name}</div>
            <div className="mt-1 inline-flex max-w-full items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-semibold uppercase tracking-normal text-rozops-text-muted">
              <span className="truncate">SKU: {p.sku || 'Not set'}</span>
            </div>
            <div className="text-sm text-rozops-text-muted break-words">{p.category || 'No category'} · per {p.unit}</div>
            <div className="mt-3 font-heading font-extrabold text-xl text-rozops-blue">₹{Number(p.default_price).toFixed(2)}</div>
            {Number(p.tax_percentage || 0) > 0 && <div className="text-xs text-rozops-text-muted mt-1">Tax {Number(p.tax_percentage).toFixed(2)}%</div>}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
          <div className="bg-white rounded-2xl shadow-medium p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-xl text-rozops-navy mb-4">{editing ? 'Edit product' : 'Add product'}</h2>
            <form onSubmit={save} className="space-y-3">
              <input required placeholder={template.productPlaceholder} className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
              <input placeholder="SKU (e.g. MILK-500ML)" className={`${inputCls} uppercase`} value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} data-testid="prod-sku" />
              {categories.length > 0 ? (
                <select className={inputCls} value={form.category} onChange={(e) => {
                  const nextCategory = categories.find(c => c.name === e.target.value);
                  const nextUnits = nextCategory?.units?.length ? nextCategory.units : defaultUnits;
                  setForm({ ...form, category: e.target.value, unit: nextUnits.includes(form.unit) ? form.unit : nextUnits[0] });
                }}>
                  <option value="">Select category</option>
                  {categories.map(category => <option key={category.id} value={category.name}>{category.name}</option>)}
                  {form.category && !categories.some(category => category.name === form.category) && <option value={form.category}>{form.category}</option>}
                </select>
              ) : (
                <input placeholder="Category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <select className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {unitOptions.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                  {form.unit && !unitOptions.includes(form.unit) && <option value={form.unit}>{form.unit}</option>}
                </select>
                <input required type="number" step="0.01" placeholder="Price" className={inputCls} value={form.default_price} onChange={(e) => setForm({ ...form, default_price: e.target.value })} data-testid="prod-price" />
              </div>
              <input type="number" min="0" step="0.01" placeholder="Tax percentage" className={inputCls} value={form.tax_percentage} onChange={(e) => setForm({ ...form, tax_percentage: e.target.value })} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={resetForm} className="px-4 py-2 rounded-xl text-rozops-text-muted">Cancel</button>
                <button type="submit" disabled={saving} data-testid="prod-submit" className="bg-rozops-blue text-white rounded-xl px-4 py-2 font-semibold disabled:opacity-60">{saving ? 'Saving...' : editing ? 'Save changes' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
